/**
 * A glyph for every tool.
 *
 * ## Why they are drawn rather than fetched
 *
 * An icon font or a sprite sheet would be a dependency, a network request and a second thing
 * to keep in step with the registry. These are a few SVG elements each, they inherit their
 * colour from the row through `currentColor` — so a selected tool's glyph turns blue with its
 * label, and nothing has to know about the palette — and they work with no network at all,
 * which for a classroom is not a small thing.
 *
 * ## The rule every one of them follows
 *
 * **The icon is a miniature of what the tool makes.** Midpoint is a segment with its middle
 * point filled in. Tangent is a circle with a line resting on it. Circumcentre is a triangle
 * with the circle round it and the dot at its centre. That is the whole design, and it is the
 * reason these can be read by someone who does not read the label: the picture is not a
 * *symbol standing for* the construction, it **is** the construction, at 15 pixels.
 *
 * It also decides the cases where a pretty icon would have been easy and wrong. Reflect could
 * be a mirror; it is a triangle and its image across a dashed axis, because that is what the
 * tool produces. A user who has seen the icon knows what will appear on the canvas.
 *
 * ## Drawn for fifteen pixels
 *
 * Which is the size that rules out detail. Every glyph is a handful of strokes on a 20-unit
 * grid with nothing thinner than a 1.4-unit line and no feature smaller than a 2-unit dot —
 * anything finer reads as grey fuzz at the size it is actually used, however good it looks
 * zoomed in.
 */

import { type JSX } from 'react';

/** A point, drawn the way the canvas draws one. */
const dot = (x: number, y: number, r = 1.9, key?: string) => (
  <circle key={key} cx={x} cy={y} r={r} fill="currentColor" stroke="none" />
);

/** A hollow point, for somewhere a construction refers to but does not create. */
const ring = (x: number, y: number, r = 1.9, key?: string) => (
  <circle key={key} cx={x} cy={y} r={r} fill="none" />
);

const line = (x1: number, y1: number, x2: number, y2: number, key?: string) => (
  <line key={key} x1={x1} y1={y1} x2={x2} y2={y2} />
);

/**
 * The triangle the four centre tools stand on, and the numbers derived from it.
 *
 * Computed rather than eyeballed, because these icons claim to *be* the construction. The
 * first draft placed each circle by eye and it showed at four times size: the circumcircle cut
 * through the sides instead of passing through the corners, and the incircle floated clear of
 * them. An icon of a wrong construction, in a program whose whole promise is that its figures
 * are true, is the one thing it must not ship.
 *
 * A(3, 15.5) B(9, 4.5) C(17, 15.5), and from those: circumcentre (10, 12.18) r 7.75; incentre
 * (9.46, 11.66) r 3.84; centroid (9.67, 11.83); orthocentre (9, 11.14).
 */
const TRIANGLE = 'M3 15.5 L9 4.5 L17 15.5 Z';

/** Faint enough to read as scaffolding, dark enough to see at fifteen pixels. */
const SCAFFOLD = 0.42;

/** The centre point itself. Small: at icon size a fat dot swallows the triangle around it. */
const CENTRE = 1.5;

const ARROWHEAD = (x: number, y: number, dx: number, dy: number, key?: string) => {
  const length = Math.hypot(dx, dy) || 1;
  const [ux, uy] = [dx / length, dy / length];
  const [px, py] = [-uy, ux];
  const back = 4.2;
  const wide = 2.4;
  return (
    <path
      key={key}
      d={
        `M${String(x)} ${String(y)} ` +
        `L${String(x - ux * back + px * wide)} ${String(y - uy * back + py * wide)} ` +
        `L${String(x - ux * back - px * wide)} ${String(y - uy * back - py * wide)} Z`
      }
      fill="currentColor"
      stroke="none"
    />
  );
};

/**
 * Every tool's glyph, by id.
 *
 * Missing ids fall back to nothing rather than to a placeholder box: a glyph that means "no
 * glyph" is worse than a row that is simply a label. The omission is caught by rendering every
 * tool's icon and demanding it draw something — see `toolIcons.test.tsx` — rather than by
 * exporting the key list, which would cost this file its Fast Refresh.
 */
const GLYPHS: Readonly<Record<string, JSX.Element>> = {
  /* ------------------------------------------------------------------- draw */
  select: (
    <>
      <path d="M5 3 L5 16 L8.6 12.6 L11 17 L13 16 L10.7 11.8 L15.5 11.4 Z" fill="currentColor" />
    </>
  ),
  point: <>{dot(10, 10, 3.2)}</>,
  segment: (
    <>
      {line(5, 14.5, 15, 5.5)}
      {dot(5, 14.5)}
      {dot(15, 5.5)}
    </>
  ),
  /*
   * A ray and a vector are not the same arrow.
   *
   * They were, in the first draft — both a sloped line with a head on it — which is two tools
   * wearing one picture. The mathematics says how to tell them apart, so the icons say it too:
   * a ray **starts** somewhere and runs on for ever, so it has a point at one end and leaves
   * the frame at the other; a vector has a definite head as well as a tail, so it carries an
   * arrow and stops inside the frame. A line, for the same reason, leaves the frame at both
   * ends and has no endpoint at all.
   */
  ray: (
    <>
      {line(4.6, 15.2, 18, 3.6)}
      {dot(4.6, 15.2)}
    </>
  ),
  line: (
    <>
      {line(2.5, 17, 17.5, 3)}
      {dot(7, 12.8)}
      {dot(13, 7.2)}
    </>
  ),
  vector: (
    <>
      {line(4.6, 15.2, 12.8, 8.1)}
      {ARROWHEAD(15.4, 5.9, 1, -0.86)}
      {dot(4.6, 15.2)}
    </>
  ),
  circle: (
    <>
      <circle cx={10} cy={10} r={6.6} fill="none" />
      {dot(10, 10, 1.7)}
    </>
  ),
  polygon: (
    <>
      <path d="M10 3.4 L16.8 8.4 L14.2 16.4 L5.8 16.4 L3.2 8.4 Z" fill="none" />
    </>
  ),
  rectangle: (
    <>
      <rect x={3.4} y={5.4} width={13.2} height={9.2} rx={0.6} fill="none" />
    </>
  ),
  ellipse: (
    <>
      <ellipse cx={10} cy={10} rx={7} ry={4.6} fill="none" />
    </>
  ),
  polyline: (
    <>
      <path d="M3.6 14.5 L7.6 7 L12.4 13 L16.4 5.5" fill="none" />
      {dot(3.6, 14.5, 1.6)}
      {dot(7.6, 7, 1.6)}
      {dot(12.4, 13, 1.6)}
      {dot(16.4, 5.5, 1.6)}
    </>
  ),
  freehand: (
    <>
      <path d="M3.4 13.6 C5.4 6.4 8 5.2 9.6 9.4 C11.2 13.6 13 14.4 16.6 7.2" fill="none" />
    </>
  ),
  text: (
    <>
      <path d="M4.6 5.4 H15.4 M10 5.4 V15.6 M7.6 15.6 H12.4" fill="none" />
    </>
  ),
  /*
   * A dashed frame with its corner grips — the thing the tool puts on the drawing, which is the
   * most direct thing an icon can say. The grips are filled so they read as grips at 20px,
   * where a hollow 2px square is a smudge.
   */
  transform: (
    <>
      <rect x={5} y={5} width={10} height={10} fill="none" strokeDasharray="2.2 1.8" rx={0.4} />
      <rect x={3.6} y={3.6} width={2.8} height={2.8} fill="currentColor" stroke="none" />
      <rect x={13.6} y={3.6} width={2.8} height={2.8} fill="currentColor" stroke="none" />
      <rect x={3.6} y={13.6} width={2.8} height={2.8} fill="currentColor" stroke="none" />
      <rect x={13.6} y={13.6} width={2.8} height={2.8} fill="currentColor" stroke="none" />
    </>
  ),

  /* -------------------------------------------------------------- construct */
  midpoint: (
    <>
      {line(4, 14, 16, 6)}
      {ring(4, 14, 1.7)}
      {ring(16, 6, 1.7)}
      {dot(10, 10, 2.6)}
    </>
  ),
  'segment-midpoint': (
    <>
      {line(4, 14, 16, 6)}
      {dot(10, 10, 2.6)}
    </>
  ),
  perpendicular: (
    <>
      {line(2.8, 14.5, 17.2, 14.5)}
      {line(10, 3, 10, 14.5)}
      <path d="M10 12 H12.5 V14.5" fill="none" />
      {dot(10, 3, 1.7)}
    </>
  ),
  parallel: (
    <>
      {line(3, 13.5, 17, 7.5)}
      {line(3, 8, 17, 2)}
      {dot(10, 5)}
    </>
  ),
  'perpendicular-bisector': (
    <>
      {line(3.5, 14, 16.5, 14)}
      {line(10, 3.6, 10, 17)}
      {dot(3.5, 14, 1.7)}
      {dot(16.5, 14, 1.7)}
      <path d="M6 12.4 L7 15.6 M13 12.4 L14 15.6" fill="none" />
    </>
  ),
  'angle-bisector': (
    <>
      <path d="M3.6 16 H17 M3.6 16 L12.2 4.8" fill="none" />
      {/* The bisector of those two arms, taken properly rather than drawn by eye. */}
      <path d="M3.6 16 L15.4 9.6" strokeDasharray="2.6 2.2" fill="none" />
      <path d="M9.2 16 A5.6 5.6 0 0 0 7 11.9" fill="none" opacity={SCAFFOLD} />
    </>
  ),
  tangent: (
    <>
      <circle cx={9} cy={12} r={5.4} fill="none" />
      {line(2.6, 4.4, 17.4, 8.6)}
      {dot(9.6, 6.6, 1.7)}
    </>
  ),
  projection: (
    <>
      {line(2.8, 15, 17.2, 15)}
      {dot(7.4, 4.6, 1.9)}
      <path d="M7.4 4.6 V15" strokeDasharray="2.2 1.9" fill="none" />
      {dot(7.4, 15, 1.7)}
      <path d="M7.4 12.6 H9.8 V15" fill="none" />
    </>
  ),
  intersection: (
    <>
      {line(3, 5, 17, 15)}
      {line(3, 15, 17, 5)}
      {dot(10, 10, 2.6)}
    </>
  ),

  /* ----------------------------------------------------------------- planes */

  /*
   * A plane is drawn the way it is drawn everywhere: a leaning parallelogram, toned. Not a
   * square, and not axis-aligned — a rectangle at this size reads as a card or a button, and the
   * lean is the whole of what says "a flat thing seen at an angle".
   */
  plane: (
    <>
      <polygon
        points="6,4 18,6 14,16 2,14"
        fill="currentColor"
        fillOpacity={0.18}
        strokeLinejoin="round"
      />
    </>
  ),

  /*
   * A plane hinged off another, with the hinge drawn solid and an arc between them.
   *
   * Three things have to read at sixteen pixels, and they are ranked. The **hinge** is the line
   * both planes share and the thing the two clicks after the first are naming, so it is the one
   * solid stroke. The **arc** is what the tool is for — an angle, not a position — and is the
   * only curve in the whole icon set at this size, which is what tells it apart from `plane-meet`
   * at a glance. The two faces are toned and unequal, because a pair of equally-lit parallelograms
   * reads as a box rather than as one plane lifted off another.
   */
  /*
   * Two leaning parallelograms crossing, with the line they share drawn solid.
   *
   * The blackboard figure itself, at sixteen pixels. The near one is toned more heavily so the
   * pair reads as two surfaces rather than as a folded card, and the shared line is the only
   * solid stroke because it is the thing the figure is usually about.
   */
  'plane-free': (
    <>
      <polygon
        points="2,12 11,9 18,12 9,15"
        fill="currentColor"
        fillOpacity={0.14}
        strokeLinejoin="round"
      />
      <polygon
        points="3,16 10,4 17,7 10,19"
        fill="currentColor"
        fillOpacity={0.26}
        strokeLinejoin="round"
      />
      {line(4, 13, 15, 10)}
    </>
  ),

  /* One plane, and a point sitting on it — which is the whole of what the tool does. */
  'plane-point': (
    <>
      <polygon
        points="2,13 11,7 18,10 9,16"
        fill="currentColor"
        fillOpacity={0.2}
        strokeLinejoin="round"
      />
      {dot(10, 12, 2)}
    </>
  ),

  'plane-turn': (
    <>
      <polygon
        points="2,14 12,11 18,15 8,18"
        fill="currentColor"
        fillOpacity={0.16}
        strokeLinejoin="round"
      />
      <polygon
        points="2,14 12,11 15,4 5,7"
        fill="currentColor"
        fillOpacity={0.28}
        strokeLinejoin="round"
      />
      {line(2, 14, 12, 11)}
      <path d="M 9,13 A 5,5 0 0 0 7,8.6" fill="none" />
    </>
  ),

  /*
   * Two of them crossing, with the giao tuyến drawn solid across both. The line is the thing the
   * tool makes, so it is the thing the eye lands on: the planes are toned faintly and it is not.
   */
  /*
   * A point above a plane, the perpendicular dropped to it, and the right-angle tick where it
   * lands — which is exactly what the tool puts on the canvas.
   */
  'plane-foot': (
    <>
      <polygon
        points="2,11 12,8 18,13 8,16"
        fill="currentColor"
        fillOpacity={0.16}
        strokeLinejoin="round"
      />
      {line(10, 3, 10, 12)}
      <polyline points="12,12 12,10" />
      {dot(10, 3, 1.8)}
    </>
  ),

  'plane-meet': (
    <>
      <polygon
        points="3,6 17,4 17,12 3,14"
        fill="currentColor"
        fillOpacity={0.14}
        strokeLinejoin="round"
      />
      <polygon
        points="6,2 14,4 14,18 6,16"
        fill="currentColor"
        fillOpacity={0.14}
        strokeLinejoin="round"
      />
      {line(6, 9, 14, 8)}
    </>
  ),

  /* ---------------------------------------------------------------- centres */
  circumcenter: (
    <>
      <circle cx={10} cy={12.18} r={7.75} fill="none" opacity={SCAFFOLD} />
      <path d={TRIANGLE} fill="none" />
      {dot(10, 12.18, CENTRE)}
    </>
  ),
  incenter: (
    <>
      <path d={TRIANGLE} fill="none" />
      <circle cx={9.46} cy={11.66} r={3.84} fill="none" opacity={SCAFFOLD} />
      {dot(9.46, 11.66, CENTRE)}
    </>
  ),
  centroid: (
    <>
      <path d={TRIANGLE} fill="none" />
      {/* Each median to the midpoint of the far side: (13,10), (10,15.5), (6,10). */}
      <path d="M3 15.5 L13 10 M9 4.5 L10 15.5 M17 15.5 L6 10" fill="none" opacity={SCAFFOLD} />
      {dot(9.67, 11.83, CENTRE)}
    </>
  ),
  orthocenter: (
    <>
      <path d={TRIANGLE} fill="none" />
      {/* The altitude from A lands at (12.16, 8.84); the one from B is vertical. */}
      <path d="M3 15.5 L12.16 8.84 M9 4.5 L9 15.5" fill="none" opacity={SCAFFOLD} />
      {dot(9, 11.14, CENTRE)}
    </>
  ),

  /* ----------------------------------------------------------------- curves */
  circumcircle: (
    <>
      <circle cx={10} cy={10} r={6.8} fill="none" />
      {dot(10, 3.2, 1.8)}
      {dot(4.2, 13.4, 1.8)}
      {dot(15.8, 13.4, 1.8)}
    </>
  ),
  'circle-center': (
    <>
      <circle cx={10} cy={10} r={6.6} fill="none" opacity={0.55} />
      {dot(10, 10, 3)}
    </>
  ),
  'arc-3-points': (
    <>
      <path d="M3.4 14.6 A8 8 0 0 1 16.6 14.6" fill="none" />
      {dot(3.4, 14.6, 1.8)}
      {dot(10, 5.6, 1.8)}
      {dot(16.6, 14.6, 1.8)}
    </>
  ),
  'arc-center': (
    <>
      <path d="M4.4 14 A7 7 0 0 1 16 11.2" fill="none" />
      <path d="M10 15.4 L4.4 14 M10 15.4 L16 11.2" fill="none" opacity={0.5} />
      {dot(10, 15.4, 2.2)}
    </>
  ),
  'ellipse-foci': (
    <>
      <ellipse cx={10} cy={10} rx={7.2} ry={4.6} fill="none" />
      {dot(5.6, 10, 1.8)}
      {dot(14.4, 10, 1.8)}
    </>
  ),

  /* -------------------------------------------------------------- transform */
  reflection: (
    <>
      <path d="M10 2.6 V17.4" strokeDasharray="2.4 2" fill="none" opacity={0.7} />
      <path d="M8.2 5 L2.6 10 L8.2 15 Z" fill="none" />
      <path d="M11.8 5 L17.4 10 L11.8 15 Z" fill="none" opacity={0.5} />
    </>
  ),
  rotation: (
    <>
      <path d="M3.6 16.4 L8 8.4 L12.4 16.4 Z" fill="none" />
      <path d="M5.4 6.2 A7.6 7.6 0 0 1 16.4 8.6" fill="none" opacity={0.75} />
      {ARROWHEAD(17, 10.2, 0.35, 1, 'head')}
      {dot(3.6, 16.4, 1.8)}
    </>
  ),
  translation: (
    <>
      <path d="M2.6 12.6 L6.4 6 L10.2 12.6 Z" fill="none" />
      <path d="M9.8 16.4 L13.6 9.8 L17.4 16.4 Z" fill="none" opacity={0.5} />
      {line(7.6, 8.6, 12, 12.4)}
      {ARROWHEAD(13.4, 13.6, 1, 0.86)}
    </>
  ),
  scaling: (
    <>
      <path d="M3.4 11.4 L6.2 6.6 L9 11.4 Z" fill="none" />
      <path d="M8.6 17 L13.4 5.6 L18.2 17 Z" fill="none" opacity={0.55} />
    </>
  ),

  /* ---------------------------------------------------------------- combine */
  'combine-union': (
    <>
      <circle cx={7.6} cy={10} r={5} fill="currentColor" opacity={0.28} stroke="none" />
      <circle cx={12.4} cy={10} r={5} fill="currentColor" opacity={0.28} stroke="none" />
      <circle cx={7.6} cy={10} r={5} fill="none" />
      <circle cx={12.4} cy={10} r={5} fill="none" />
    </>
  ),
  'combine-intersect': (
    <>
      <path
        d="M10 5.4 A5 5 0 0 1 10 14.6 A5 5 0 0 1 10 5.4 Z"
        fill="currentColor"
        opacity={0.4}
        stroke="none"
      />
      <circle cx={7.6} cy={10} r={5} fill="none" />
      <circle cx={12.4} cy={10} r={5} fill="none" />
    </>
  ),
  'combine-subtract': (
    <>
      <path
        d="M7.6 5 A5 5 0 0 0 7.6 15 A5 5 0 0 0 10 14.6 A5 5 0 0 1 10 5.4 A5 5 0 0 0 7.6 5 Z"
        fill="currentColor"
        opacity={0.35}
        stroke="none"
      />
      <circle cx={7.6} cy={10} r={5} fill="none" />
      <circle cx={12.4} cy={10} r={5} fill="none" strokeDasharray="2.2 1.8" opacity={0.7} />
    </>
  ),

  /* --------------------------------------------------------------- annotate */
  'angle-mark': (
    <>
      <path d="M4 15.6 H17 M4 15.6 L14.6 4.6" fill="none" />
      <path d="M11 15.6 A7 7 0 0 0 8.9 10.7" fill="none" />
      {dot(4, 15.6, 1.7)}
    </>
  ),
  /*
   * The same angle, with the thing that distinguishes the tool: it writes the measure.
   *
   * A numeral was tried and is unreadable at fifteen pixels — two digits inside an angle is
   * four grey specks. The degree sign alone is one glyph, it is what a measure looks like, and
   * it survives being small.
   */
  'angle-value': (
    <>
      <path d="M3.4 16 H14 M3.4 16 L11.6 6" fill="none" />
      <path d="M9 16 A5.6 5.6 0 0 0 7 12.2" fill="none" opacity={SCAFFOLD} />
      <text
        x={11.4}
        y={13.6}
        fontSize={9}
        fontFamily="system-ui, sans-serif"
        fill="currentColor"
        stroke="none"
      >
        °
      </text>
    </>
  ),

  /* ----------------------------------------------------------------- solids */
  'solid-cube': (
    <>
      <path d="M3.6 7.4 L9.4 4.4 L16.4 7.4 L16.4 13.4 L10.6 16.4 L3.6 13.4 Z" fill="none" />
      <path d="M3.6 7.4 L10.6 10.4 L16.4 7.4 M10.6 10.4 L10.6 16.4" fill="none" />
    </>
  ),
  'solid-cuboid': (
    <>
      <path d="M3.2 8.2 L8.4 5.6 L16.8 5.6 L16.8 12.4 L11.6 15 L3.2 15 Z" fill="none" />
      <path d="M3.2 8.2 L11.6 8.2 L16.8 5.6 M11.6 8.2 L11.6 15" fill="none" />
    </>
  ),
  'solid-prism': (
    <>
      <path d="M4 6.6 L10 4 L16 6.6 L16 13 L10 16.2 L4 13 Z" fill="none" />
      <path d="M4 6.6 L10 9.4 L16 6.6 M10 9.4 L10 16.2" fill="none" opacity={0.55} />
    </>
  ),
  'solid-pyramid': (
    <>
      <path d="M10 3.2 L17 13.4 L10 16.8 L3 13.4 Z" fill="none" />
      <path
        d="M3 13.4 L11.4 11 L17 13.4 M11.4 11 L10 3.2 M11.4 11 L10 16.8"
        fill="none"
        opacity={0.55}
      />
    </>
  ),
  'solid-cylinder': (
    <>
      <ellipse cx={10} cy={5.6} rx={5.6} ry={2.2} fill="none" />
      <path d="M4.4 5.6 V14.4 M15.6 5.6 V14.4" fill="none" />
      <path d="M4.4 14.4 A5.6 2.2 0 0 0 15.6 14.4" fill="none" />
      <path
        d="M4.4 14.4 A5.6 2.2 0 0 1 15.6 14.4"
        fill="none"
        opacity={0.4}
        strokeDasharray="2 1.8"
      />
    </>
  ),
  'solid-cone': (
    <>
      <path d="M10 3.2 L15.6 14 M10 3.2 L4.4 14" fill="none" />
      <path d="M4.4 14 A5.6 2.2 0 0 0 15.6 14" fill="none" />
      <path d="M4.4 14 A5.6 2.2 0 0 1 15.6 14" fill="none" opacity={0.4} strokeDasharray="2 1.8" />
    </>
  ),
  'solid-sphere': (
    <>
      <circle cx={10} cy={10} r={6.6} fill="none" />
      <ellipse cx={10} cy={10} rx={6.6} ry={2.4} fill="none" opacity={0.5} />
    </>
  ),
  'solid-frustum': (
    <>
      <ellipse cx={10} cy={6} rx={3.4} ry={1.5} fill="none" />
      <path d="M6.6 6 L3.6 14 M13.4 6 L16.4 14" fill="none" />
      <path d="M3.6 14 A6.4 2.4 0 0 0 16.4 14" fill="none" />
      <path d="M3.6 14 A6.4 2.4 0 0 1 16.4 14" fill="none" opacity={0.4} strokeDasharray="2 1.8" />
    </>
  ),
  'rotate-solid': (
    <>
      <path
        d="M4.6 9 L9.2 6.6 L14.8 9 L14.8 13.4 L10.2 15.8 L4.6 13.4 Z"
        fill="none"
        opacity={0.65}
      />
      <path d="M4.6 9 L10.2 11.4 L14.8 9" fill="none" opacity={0.65} />
      <path d="M4.2 6 A6.4 6.4 0 0 1 15 4.4" fill="none" />
      {ARROWHEAD(16.4, 5.2, 1, 0.7)}
    </>
  ),
};

export interface ToolIconProps {
  readonly tool: string;
  readonly size?: number;
}

/**
 * The glyph for a tool, or nothing when it has none.
 *
 * `aria-hidden`, always: the button beside it already carries the tool's name, and a screen
 * reader that announced both would say everything twice.
 */
export function ToolIcon({ tool, size = 15 }: ToolIconProps) {
  const glyph = GLYPHS[tool];
  if (glyph === undefined) return null;

  return (
    <svg
      aria-hidden
      focusable="false"
      viewBox="0 0 20 20"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      {glyph}
    </svg>
  );
}
